(ns app.domain.form.form-editor-util
  (:require [clojure.string :as str]
            [potpuri.core :as p]))

;;
;; Helper functions for map presentation of form schemas
;;


(defn child-key? [k]
  (number? k))


(defn form-el->children
  ([form-el]
   (form-el->children (constantly true) form-el))
  ([filter-pred form-el]
   (->> (p/filter-keys child-key? form-el)
        vals
        (filter filter-pred)
        (sort-by :order)
        vec)))


(defn form-el->parent-path [{:keys [path]}]
  (pop path))


;;
;; Functions to translate a form schema into map-presentation and back
;;


(defn- subschema-is-element? [subschema]
  (and (vector? subschema)
       (-> subschema first keyword?)))


(defn- subschema->type
  "In the map representation of a form schema 'type-attribute' is used instead of tag.
  type = tag for the most form elements but there are some minor differences:
  - Strings (string children) inside a form element are viewed as actual child elements and for these elements type = :text.
  (schema-data->map [p {} 'foo']) =
  {1 {:type       :p
      :attributes {}
      :id         1
      :path       [1]
      :order      0
      2           {:path [1 2] :text-value 'foo' :type :text}}}

  - vector of elements (e.g. [[:input {}] [:p {}]]) are also viewed as form elements
  (schema-data->map [[:p {}]]) =
  {1 {:type :pseudo-list-item
      :id   1
      :path [1]
      2     {:type       :p
             :id         2
             :path       [1 2]
             :attributes {}}}})

  - for number inputs, type = :number-input"
  [subschema]
  (let [element?    (subschema-is-element? subschema)
        [tag attrs] (when element? subschema)]
    (cond
      (string? subschema)
      :text

      (and (vector? subschema) (not element?))
      :list-row

      (and (= tag :input)
           (map? attrs)
           (->  attrs :type (= :number)))
      :number-input

      :else
      tag)))


(defn- type->tag [type]
  (if (= type :number-input)
    :input
    type))


(defn- subschema->map [*id subschema path-to-parent order]
  (let [id                   (swap! *id inc)
        curr-path            (conj (or path-to-parent []) id)
        [_ attrs & children] (when (subschema-is-element? subschema) subschema)
        type                 (subschema->type subschema)]
    (hash-map
      id
      (apply merge
             (->> {:order      order
                   :id         id
                   :type       type
                   :attributes (when (map? attrs) attrs)
                   :path       curr-path
                   :text-value (when (string? subschema) subschema)}
                  (p/remove-vals nil?))
             (->> (if (= type :list-row)
                    ;; vector of elements e.g. [[:input {}] [:p {}]] are presented/viewed as form elements
                    subschema
                    children)
                  (map-indexed (fn [i child] (subschema->map *id child curr-path i))))))))


(defn schema-data->map
  "Returns map representation of the form schema.

  returns a structure that is pattern:
  {1 {:type       :form
      :attributes {}
      :path       [1]
      :order      0
      :id         1}
      2           {:type       :section
                   :attributes {:label 'Foobar'}
                   :path       [1 2]
                   :order      0
                   :id         2}
                   3           {:type       :form-row
                                :attributes {}
                                :path       [1 2 3]
                                :order      0
                                :id         3
                                4           {:type       :form-row
                                             :attributes {}
                                             :path       [1 2 3 4]
                                             :order      0
                                             :id         4
                                             5           {:type       :input
                                                          :attributes {:label  'Foo'
                                                                       :field  :foo}
                                                          :path       [1 2 3 4 5]
                                                          :order      0
                                                          :id         5}}}}"
  [schema]
  (subschema->map (atom -1) schema nil 0))


(defn- map->schema-data [{:keys [attributes type] :as item}]
  (cond
    (= type :list-row)
    (mapv map->schema-data (form-el->children item))

    (= type :text)
    (-> item :text-value)

    :else
    (cond-> [(type->tag type)]
      (some? attributes)
      (conj attributes)

      :always
      (into (->> (form-el->children item)
                 (map map->schema-data))))))


(defn map-presentation->schema-data
  "Returns the schema data hiccup of the form schema data"
  [form-map]
  (-> (get form-map 0)
      map->schema-data))

(defmulti resolve-child-values :type)


(defmethod resolve-child-values :default
  [_]
  [])


(defmethod resolve-child-values :select
  [form-el]
  (-> form-el :attributes :form.field/valid-values))


(defmethod resolve-child-values :radio-button-group
  [form-el]
  (->> (form-el->children form-el)
       (map (comp :value :attributes))))

(defn form-el->child-values
  [form-el]
  (->> (resolve-child-values form-el)
       (filterv (complement str/blank?))))

(defn map-presentation->form-fields [form-map]
  (let [children (form-el->children form-map)]
    (into (filter (comp :field :attributes) children)
          (mapcat map-presentation->form-fields children))))
